/**
 * 表格单元格组件
 */

import React, { useMemo, useCallback } from 'react';
import { CellState, CellEditorConfig } from '../types/excelTableTypes';
import { validatePageNumberInput } from '../../../../utils/pageNumberUtils';
import InputRecommendNEW from '../../../../components/InputRecommend/InputRecommendNEW';
import './TableCell.css';

/** 场次表 sceneSet / sceneLocation：原生 input + 推荐菜单（数据由 BreakdownTable 按行传入） */
export interface TableCellInputRecommendConfig {
  fieldType: 'sceneSet' | 'sceneLocation';
  dataSource: Array<string | undefined | null>;
  sceneId: string;
  rowIndex: number;
  onAutoFillLocation?: (sceneId: string, sceneSetValue: string) => void;
}

interface TableCellProps {
  row: number;
  col: number;
  value: any;
  displayValue?: React.ReactNode;
  state: CellState;
  editor?: CellEditorConfig;
  rowId: string;
  colKey: string;
  record: any;
  isEditing: boolean;
  cellStyle?: React.CSSProperties;
  className?: string; // 列的 className（如 breakdown-code-cell）
  onCellClick: (row: number, col: number) => void;
  onCellDoubleClick: (row: number, col: number) => void;
  onCellValueChange: (row: number, col: number, value: any) => void;
  onCellEditEnd: (row: number, col: number, value: any, skipSelection?: boolean) => Promise<void>;
  scenesTableElementRefs?: React.MutableRefObject<Map<string, HTMLElement>>;
  /** 编辑态且为场景/地点列时挂载 InputRecommendNEW */
  inputRecommend?: TableCellInputRecommendConfig;
}

export const TableCell: React.FC<TableCellProps> = ({
  row,
  col,
  value,
  displayValue,
  state,
  editor,
  rowId,
  colKey,
  record,
  isEditing,
  cellStyle,
  className,
  onCellClick,
  onCellDoubleClick,
  onCellValueChange,
  onCellEditEnd,
  scenesTableElementRefs,
  inputRecommend,
}) => {
  const cellRef = React.useRef<HTMLTableCellElement | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const isComposingRef = React.useRef(false); // 标记是否正在使用输入法组合输入
  const isTabNavigatingRef = React.useRef(false); // 标记是否正在 Tab 键导航
  /** 父级 breakdown 更新后受控 input 重绘时恢复光标，避免在中间编辑时光标跳到末尾 */
  const pendingSelectionRef = React.useRef<{ start: number; end: number } | null>(null);
  const captureInputSelection = React.useCallback((el: HTMLInputElement | null) => {
    if (!el) return;
    pendingSelectionRef.current = {
      start: el.selectionStart ?? 0,
      end: el.selectionEnd ?? el.selectionStart ?? 0,
    };
  }, []);
  const [localValue, setLocalValue] = React.useState<string>(''); // 本地输入值，用于输入法输入
  const lastRecommendPickRef = React.useRef<string | null>(null);

  const handleRecommendSelect = useCallback(
    (text: string) => {
      lastRecommendPickRef.current = text;
      setLocalValue(text);
      onCellValueChange(row, col, text);
    },
    [row, col, onCellValueChange],
  );

  const handleRecommendFinishEdit = useCallback(async () => {
    const v =
      lastRecommendPickRef.current ?? inputRef.current?.value ?? localValue ?? '';
    lastRecommendPickRef.current = null;
    await onCellEditEnd(row, col, v);
  }, [row, col, onCellEditEnd, localValue]);

  React.useLayoutEffect(() => {
    if (!isEditing) {
      pendingSelectionRef.current = null;
      return;
    }
    const input = inputRef.current;
    const pending = pendingSelectionRef.current;
    if (!input || pending === null) return;
    if (document.activeElement !== input) {
      pendingSelectionRef.current = null;
      return;
    }
    const len = input.value.length;
    input.setSelectionRange(
      Math.min(Math.max(pending.start, 0), len),
      Math.min(Math.max(pending.end, 0), len),
    );
    pendingSelectionRef.current = null;
  }, [isEditing, localValue, value]);

  // 根据状态应用样式类
  const cellClassName = useMemo(() => {
    const classes = ['excel-table-cell'];
    if (className) classes.push(className); // 添加列的 className（如 breakdown-code-cell）
    if (state === 'selected') classes.push('cell-selected');
    if (state === 'editing') classes.push('cell-editing');
    if (state === 'hover') classes.push('cell-hover');
    return classes.join(' ');
  }, [state, className]);

  // 注意：鼠标交互（单击选中、双击编辑）已由统一导航系统处理（useUnifiedMouseInteraction）
  // 这里只保留业务逻辑回调，不阻止事件冒泡，让统一鼠标交互能够接收到事件
  const handleClick = useCallback((e: React.MouseEvent) => {
    // 如果正在编辑状态，不处理点击事件（让输入框自己处理）
    if (isEditing) {
      return;
    }
    
    // 不阻止事件冒泡，让统一鼠标交互能够接收到事件
    // e.stopPropagation(); // 已移除，让事件冒泡到 document 级别
    
    // 调用业务逻辑回调（状态管理由统一鼠标交互处理）
    onCellClick(row, col);
  }, [row, col, onCellClick, isEditing]);

  // 注意：双击编辑已由统一导航系统处理
  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    // 如果正在编辑状态，不处理双击事件（让输入框自己处理）
    if (isEditing) {
      return;
    }
    
    // 不阻止事件冒泡，让统一鼠标交互能够接收到事件
    // e.stopPropagation(); // 已移除，让事件冒泡到 document 级别
    
    // 调用业务逻辑回调（状态管理由统一鼠标交互处理）
    onCellDoubleClick(row, col);
  }, [row, col, onCellDoubleClick, isEditing]);

  // 注意：双击检测和状态管理已由统一鼠标交互处理，不再需要本地定时器
  // 注意：光标定位已由统一鼠标交互的 activateFieldEdit 处理，不再需要本地 setCursorPosition 函数

  // 处理输入框获得焦点
  const handleInputFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    // 注意：双击时的光标定位已由统一鼠标交互的 activateFieldEdit 处理
    // 这里不再需要处理双击位置记录
  }, []);

  // 进入编辑状态时，初始化本地值
  // 注意：聚焦和光标定位已由统一鼠标交互的 activateFieldEdit 处理，这里不再自动聚焦
  React.useEffect(() => {
    if (isEditing) {
      // 只有在 localValue 未初始化时才设置，避免覆盖用户正在输入的内容
      if (localValue === '') {
        setLocalValue(value || '');
      }
      isComposingRef.current = false; // 重置组合状态
      
      // 注意：不再在这里自动聚焦输入框，因为：
      // 1. 双击时，统一鼠标交互的 activateFieldEdit 会处理聚焦和光标定位
      // 2. Enter 键时，统一键盘导航的 focusAndActivatePosition 会处理聚焦
      // 3. 自动聚焦可能会覆盖统一系统的光标定位逻辑
    } else {
      // 退出编辑状态时，清空本地值
      // 注意：不清空 localValue，让它在下次进入编辑时使用新的 value 初始化
      // 但是，如果 value 已经更新，localValue 应该被清空，以便下次使用新值
      setLocalValue('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, value]);

  // 处理输入框值变化
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target) return;
    
    let newValue = e.target.value || '';
    
    // 页数字段：进行输入验证和全角句号转换
    if (colKey === 'scenePagesNumber') {
      // 将全角句号转换为半角句号（处理中文输入法下的句号）
      newValue = newValue.replace(/。/g, '.');
      
      // 如果转换后值有变化，更新输入框的值
      if (newValue !== e.target.value) {
        const cursorPosition = e.target.selectionStart || 0;
        e.target.value = newValue;
        // 恢复光标位置
        setTimeout(() => {
          e.target.setSelectionRange(cursorPosition, cursorPosition);
        }, 0);
      }
      
      // 验证输入是否合法
      if (!validatePageNumberInput(newValue)) {
        // 验证失败，阻止更新，恢复输入框的值
        const currentDisplayValue = localValue !== '' ? localValue : (value || '');
        const cursorPosition = e.target.selectionStart || 0;
        e.target.value = currentDisplayValue;
        // 恢复光标位置
        setTimeout(() => {
          const newPosition = Math.min(cursorPosition, currentDisplayValue.length);
          e.target.setSelectionRange(newPosition, newPosition);
        }, 0);
        return;
      }
    }
    
    // 如果正在使用输入法组合输入，只更新本地状态
    if (isComposingRef.current) {
      captureInputSelection(e.target);
      setLocalValue(newValue);
      return;
    }
    
    // 非组合输入时，立即更新
    captureInputSelection(e.target);
    setLocalValue(newValue);
    onCellValueChange(row, col, newValue);
  }, [row, col, colKey, localValue, value, onCellValueChange, captureInputSelection]);

  // 输入法组合开始
  const handleCompositionStart = useCallback(() => {
    isComposingRef.current = true;
  }, []);

  // 输入法组合更新
  const handleCompositionUpdate = useCallback((e: React.CompositionEvent<HTMLInputElement>) => {
    // 更新本地状态，但不触发 onCellValueChange
    if (e.currentTarget) {
      captureInputSelection(e.currentTarget);
      setLocalValue(e.currentTarget.value || '');
    }
  }, [captureInputSelection]);

  // 输入法组合结束
  const handleCompositionEnd = useCallback((e: React.CompositionEvent<HTMLInputElement>) => {
    isComposingRef.current = false;
    if (!e.currentTarget) return;
    
    captureInputSelection(e.currentTarget);
    const finalValue = e.currentTarget.value || '';
    setLocalValue(finalValue);
    // 组合输入完成后，更新实际值
    onCellValueChange(row, col, finalValue);
  }, [row, col, onCellValueChange, captureInputSelection]);

  // 处理输入框失焦
  const handleInputBlur = useCallback(async (e: React.FocusEvent<HTMLInputElement>) => {
    // 如果正在组合输入，先结束组合
    if (isComposingRef.current) {
      isComposingRef.current = false;
    }
    
    // 如果正在 Tab 键导航，不处理 blur（由 Tab 导航逻辑处理）
    if (isTabNavigatingRef.current) {
      isTabNavigatingRef.current = false;
      return;
    }
    
    // 失焦时始终保存编辑内容（包括点击其他单元格、点击表格外等）
    // 注：原先的 isTabBlur 判断会误判「点击其他单元格」为 Tab 导航，导致不保存
    // 优先使用 e.target.value（DOM 当前值），fallback 到 localValue
    const finalValue = (e.target ? e.target.value : '') || localValue;
    await onCellEditEnd(row, col, finalValue);
  }, [row, col, onCellEditEnd, localValue]);

  // 处理输入框按键
  const handleInputKeyDown = useCallback(async (e: React.KeyboardEvent<HTMLInputElement>) => {
    // 注意：Tab 导航已由统一导航系统处理
    if (e.key === 'Enter') {
      // 回车键：完成编辑并退出编辑状态
      e.preventDefault();
      e.stopPropagation();
      
      // 如果正在组合输入，先结束组合
      if (isComposingRef.current) {
        isComposingRef.current = false;
      }
      
      // 保存输入框引用，因为在异步操作后可能无法访问 e.currentTarget
      const inputElement = e.currentTarget;
      const finalValue = localValue || inputElement.value;
      
      // 完成编辑
      await onCellEditEnd(row, col, finalValue);
      
      // 失焦以退出编辑状态（ExcelTable 的 handleCellEditEnd 会设置选中状态）
      // 使用 inputRef 或 inputElement，确保元素存在
      if (inputRef.current) {
        inputRef.current.blur();
      } else if (inputElement && document.activeElement === inputElement) {
        inputElement.blur();
      }
    }
  }, [row, col, onCellEditEnd, localValue]);

  // 自定义编辑器的 onBlur：从输入框获取最新值
  const handleCustomBlur = useCallback(() => {
    // 查找输入框元素，获取最新值
    const inputElement = cellRef.current?.querySelector('input[data-scene-id], input[data-row-id]') as HTMLInputElement;
    const latestValue = inputElement?.value ?? value;
    // 完成编辑，进入选中状态（skipSelection = false，默认值）
    onCellEditEnd(row, col, latestValue, false);
  }, [row, col, onCellEditEnd, value]);

  // 渲染单元格内容
  const renderCellContent = () => {
    if (isEditing && editor) {
      // 编辑状态：渲染编辑器
      if (editor.type === 'text' || editor.type === 'number') {
        // 在编辑状态下，优先使用本地值（即使为空字符串），避免被外部值覆盖
        // 如果 localValue 已初始化（不为空字符串），使用 localValue
        // 如果 localValue 未初始化（为空字符串），使用 value
        // 这样可以确保：
        // 1. 编辑时，用户输入的值不会被外部值覆盖
        // 2. 删除值时，localValue 为空字符串，不会被 value（旧值）覆盖
        const inputValue = isEditing 
          ? (localValue !== '' ? localValue : (value || ''))
          : (value || '');
        
        const setInputRef = (el: HTMLInputElement | null) => {
          if (inputRef.current !== el) {
            inputRef.current = el;
          }
          if (scenesTableElementRefs && el) {
            const key = `${row}-${col}`;
            scenesTableElementRefs.current.set(key, el);
            (el as any).__selectText = true;
          } else if (scenesTableElementRefs && !el) {
            const key = `${row}-${col}`;
            scenesTableElementRefs.current.delete(key);
          }
        };

        return (
          <>
            <input
              ref={setInputRef}
              type={editor.type === 'number' ? 'number' : 'text'}
              inputMode={editor.inputMode as React.InputHTMLAttributes<HTMLInputElement>['inputMode']}
              value={inputValue}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onClick={(e) => {
                e.stopPropagation();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onCompositionStart={handleCompositionStart}
              onCompositionUpdate={handleCompositionUpdate}
              onCompositionEnd={handleCompositionEnd}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              className="excel-cell-input"
              data-row={row}
              data-col={col}
              data-row-id={rowId}
              data-col-key={colKey}
              {...(inputRecommend
                ? {
                    'data-scene-id': rowId,
                    'data-field': colKey,
                  }
                : {})}
            />
            {inputRecommend ? (
              <InputRecommendNEW
                fieldType={inputRecommend.fieldType}
                sceneId={inputRecommend.sceneId}
                dataSource={inputRecommend.dataSource}
                variant="table"
                rowIndex={inputRecommend.rowIndex >= 0 ? inputRecommend.rowIndex : undefined}
                targetInputRef={inputRef}
                onSelectValue={handleRecommendSelect}
                onAutoFillLocation={inputRecommend.onAutoFillLocation}
                onFinishEdit={handleRecommendFinishEdit}
              />
            ) : null}
          </>
        );
      } else if (editor.type === 'custom' && editor.component) {
        const CustomEditor = editor.component;
        return (
          <CustomEditor
            value={value}
            onChange={(newValue: any) => onCellValueChange(row, col, newValue)}
            onBlur={handleCustomBlur}
            onCellEditEnd={onCellEditEnd}
            row={row}
            col={col}
            rowId={rowId}
            colKey={colKey}
            record={record}
            isEditing={isEditing}
            {...editor.props}
          />
        );
      }
    }

    // 非编辑状态：显示内容
    return (
      <div className="excel-cell-content">
        {displayValue !== undefined ? displayValue : (value ?? '')}
      </div>
    );
  };

  // 计算 tabIndex：选中状态时可聚焦，编辑状态时不可聚焦（输入框会处理焦点）
  const tabIndex = state === 'selected' ? 0 : -1;

  return (
    <td
      ref={(el) => {
        // 保存到本地 ref（回调 ref 中可以直接赋值）
        cellRef.current = el;
        // 注册到统一导航系统的元素引用映射（对于非输入框单元格，使用 td 元素）
        if (scenesTableElementRefs) {
          const key = `${row}-${col}`;
          if (el && !editor) {
            scenesTableElementRefs.current.set(key, el);
          } else if (!el && !editor) {
            // 元素卸载时清理引用
            scenesTableElementRefs.current.delete(key);
          }
        }
      }}
      className={cellClassName}
      style={cellStyle}
      data-row={row}
      data-col={col}
      data-row-id={rowId}
      data-col-key={colKey}
      tabIndex={tabIndex}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
    >
      {renderCellContent()}
    </td>
  );
};
